import React, { createContext, useContext, useState, useEffect } from 'react';
import { xhrRequest } from '../lib/xhrRequest';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await fetch(`${API}/users/me`, {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(storedToken);
          } else {
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [API]);

  const login = async (email, password) => {
    const { ok, status, data } = await xhrRequest('POST', `${API}/auth/login`, {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!ok) {
      throw new Error(data.detail || `Login failed (${status})`);
    }

    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const signup = async (email, password, displayName, serviceTitle) => {
    const { ok, status, data } = await xhrRequest('POST', `${API}/auth/signup`, {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, displayName, serviceTitle }),
    });

    if (!ok) {
      throw new Error(data.detail || `Signup failed (${status})`);
    }

    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      signup,
      logout,
      updateUser,
      refreshUser,
      isAuthenticated: !!token
    }}>
      {children}
    </AuthContext.Provider>
  );
};
